import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Button, Form, Modal, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import {
  useListEntrepreneurUsersQuery,
  useCreateEntrepreneurUserMutation,
  useDeactivateEntrepreneurUserMutation,
  useSetEntrepreneurUserRoleMutation,
  useResetEntrepreneurUserPasswordMutation,
  useImpersonateEntrepreneurUserMutation,
  useRequestEntrepreneurClaudeSeatMutation,
  useWithdrawEntrepreneurClaudeSeatMutation,
  useGetEntrepreneurClaudeCertUrlMutation,
} from '../../app/api';
import { setB2BToken, setB2BImpersonator } from '../../b2b/auth';
import { brand, FilterBar, SeatBadge, SEAT_PENDING, SEAT_REQUESTABLE, runAction } from './common';

/* Firm-scoped member management, the main section of the firm's hub page
 * (FirmDetail). The people who log in to this firm's hub: add (which sends an
 * invite), change role, reset password, deactivate.
 *
 * Hub membership and a Claude seat are separate. Adding someone gives them the
 * hub (announcements, resources, training) and nothing else; the Claude column
 * shows whether a seat has been asked for, and staff can open or withdraw a
 * request here on the firm's behalf. Approving a request and charging for it
 * happens on the firm's Claude page (ClaudeFirmDetail). Bulk upload stays on the cross-firm Firms list
 * (the sheet spans firms).
 */

const blankUser = { email: '', first_name: '', last_name: '', role: 'B2B_STANDARD', send_email: true, request_claude: false };

export default function FirmUsersSection({ firmId, firmName, onChange, canImpersonate, claudeSignedUp, transitioning }) {
  const navigate = useNavigate();
  const { data: allUsers = [], isLoading, refetch } = useListEntrepreneurUsersQuery({ firm_id: firmId });
  const [search, setSearch] = useState('');
  const users = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(u => `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase().includes(q));
  }, [allUsers, search]);

  const [createUser] = useCreateEntrepreneurUserMutation();
  const [deactivateUser] = useDeactivateEntrepreneurUserMutation();
  const [setRole] = useSetEntrepreneurUserRoleMutation();
  const [resetPassword] = useResetEntrepreneurUserPasswordMutation();
  const [requestSeat] = useRequestEntrepreneurClaudeSeatMutation();
  const [withdrawSeat] = useWithdrawEntrepreneurClaudeSeatMutation();
  const [getCertUrl] = useGetEntrepreneurClaudeCertUrlMutation();

  const memberName = (u) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;

  const onRequestSeat = (u) => {
    if (!window.confirm(`Request a Claude seat for ${memberName(u)}? They'll join the seat queue and need to finish Claude certification before it can be approved.`)) return;
    runAction(requestSeat, { uuid: u.uuid }, refreshAll);
  };
  const onWithdrawSeat = (u) => {
    if (!window.confirm(`Withdraw the Claude seat request for ${memberName(u)}? They stay in the hub.`)) return;
    runAction(withdrawSeat, { uuid: u.uuid }, refreshAll);
  };
  const viewCertificate = async (uuid) => {
    try {
      const res = await getCertUrl({ uuid }).unwrap();
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch { window.alert('Could not open that certificate.'); }
  };

  const refreshAll = async () => {
    await refetch();
    if (onChange) await onChange();
  };

  // ── Add user modal (firm fixed to this firm) ──
  const [showUser, setShowUser] = useState(false);
  const [userForm, setUserForm] = useState(blankUser);
  const [userSaving, setUserSaving] = useState(false);
  const [userError, setUserError] = useState(null);
  const [createdInfo, setCreatedInfo] = useState(null);

  const openUser = () => { setUserForm(blankUser); setUserError(null); setCreatedInfo(null); setShowUser(true); };

  const submitUser = async (e) => {
    e.preventDefault();
    if (!userForm.email.trim()) { setUserError('Email is required.'); return; }
    setUserSaving(true); setUserError(null);
    try {
      const res = await createUser({
        email: userForm.email.trim(),
        first_name: userForm.first_name.trim() || null,
        last_name: userForm.last_name.trim() || null,
        firm_id: firmId,
        role: userForm.role,
        send_email: userForm.send_email,
        request_claude: !transitioning && userForm.request_claude,
      }).unwrap();
      setCreatedInfo({
        email: res.user?.email,
        claude_requested: res.user?.provision_status === 'REQUESTED',
        temp_password: res.temp_password,
        email_sent: res.email_sent,
        email_error: res.email_error,
      });
      await refreshAll();
    } catch (err) {
      setUserError(err?.data?.detail || 'Could not create user.');
    }
    setUserSaving(false);
  };

  // ── Reset password modal ──
  const [resetTarget, setResetTarget] = useState(null);
  const [resetSendEmail, setResetSendEmail] = useState(true);
  const [resetSaving, setResetSaving] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const openReset = (u) => { setResetTarget(u); setResetSendEmail(true); setResetError(null); setResetResult(null); };

  const submitReset = async (e) => {
    e.preventDefault();
    setResetSaving(true); setResetError(null);
    try {
      const res = await resetPassword({ uuid: resetTarget.uuid, send_email: resetSendEmail }).unwrap();
      setResetResult({ temp_password: res.temp_password, email_sent: res.email_sent, email_error: res.email_error });
      await refetch();
    } catch (err) {
      setResetError(err?.data?.detail || 'Could not reset the password.');
    }
    setResetSaving(false);
  };

  // ── Row actions ──
  const onChangeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await setRole({ uuid: u.uuid, role }).unwrap();
      await refetch();
    } catch { /* leave the select as-is; a refetch would revert it */ }
  };

  const onDeactivate = async (u) => {
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
    if (!window.confirm(`Remove ${name}? They will be deactivated and hidden.`)) return;
    try {
      await deactivateUser({ uuid: u.uuid }).unwrap();
      await refreshAll();
    } catch { window.alert('Could not remove the user.'); }
  };

  // ── Log in as (Prosperity admin support tool) ──
  const [impersonate] = useImpersonateEntrepreneurUserMutation();
  const [impersonatingUuid, setImpersonatingUuid] = useState(null);

  const onImpersonate = async (u) => {
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
    if (!window.confirm(`Log in as ${name}? You'll enter their Entrepreneur workspace — use “Return to admin” there to come back.`)) return;
    setImpersonatingUuid(u.uuid);
    try {
      const res = await impersonate({ uuid: u.uuid }).unwrap();
      setB2BToken(res.token);
      setB2BImpersonator({ target_email: res.target_email || u.email });
      navigate('/entrepreneur/dashboard');
    } catch (err) {
      window.alert(err?.data?.detail || 'Could not log in as this user.');
      setImpersonatingUuid(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <Button size="sm" style={{ background: brand.deepBlue, border: 'none' }} onClick={openUser}>
          <i className="bi bi-person-plus me-1" />Add member
        </Button>
        <FilterBar search={search} onSearchChange={setSearch} placeholder="Search name or email…" />
      </div>

      {isLoading ? (
        <div className="text-center p-4"><Spinner size="sm" /></div>
      ) : users.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>
          <i className="bi bi-people" style={{ fontSize: '1.75rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
          {allUsers.length === 0 ? 'No members yet. Add people to send their hub login invites.' : 'No members match your search.'}
        </div>
      ) : (
        <Table hover responsive className="mb-0" style={{ fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ background: brand.deepBlue, color: 'white' }}>
              <th style={{ padding: '0.6rem 0.9rem', border: 'none' }}>Name</th>
              <th style={{ padding: '0.6rem 0.9rem', border: 'none' }}>Email</th>
              <th style={{ padding: '0.6rem 0.9rem', border: 'none' }}>Role</th>
              <th style={{ padding: '0.6rem 0.9rem', border: 'none' }}>Hub login</th>
              <th style={{ padding: '0.6rem 0.9rem', border: 'none' }}>Claude</th>
              <th style={{ padding: '0.6rem 0.9rem', border: 'none', textAlign: 'right' }} />
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.uuid}>
                <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: brand.deepBlue, fontWeight: 600 }}>
                  {[u.first_name, u.last_name].filter(Boolean).join(' ') || <span style={{ color: brand.charcoal }}>—</span>}
                </td>
                <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal }}>{u.email}</td>
                <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle' }}>
                  <Form.Select size="sm" value={u.role || 'B2B_STANDARD'} onChange={e => onChangeRole(u, e.target.value)} style={{ maxWidth: 130, fontSize: '0.82rem' }}>
                    <option value="B2B_ADMIN">Admin</option>
                    <option value="B2B_STANDARD">Standard</option>
                  </Form.Select>
                </td>
                <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle' }}>
                  {u.needs_password_change
                    ? <Badge bg="warning" text="dark">Pending first login</Badge>
                    : <Badge bg="success">Active</Badge>}
                </td>
                <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <SeatBadge status={u.provision_status} />
                    {u.provision_status !== 'NONE' && (
                      u.claude_certified
                        ? <span title="Claude certification complete" style={{ color: brand.green, fontSize: '0.72rem', fontWeight: 700 }}><i className="bi bi-patch-check-fill me-1" />Certified</span>
                        : <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>Not certified</span>
                    )}
                    {u.has_certificate && (
                      <Button variant="link" size="sm" onClick={() => viewCertificate(u.uuid)} title="Open the uploaded certificate" style={{ padding: 0, fontSize: '0.72rem', color: brand.tealBlue, textDecoration: 'none' }}>
                        <i className="bi bi-file-earmark-check" />
                      </Button>
                    )}
                  </div>
                </td>
                <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {/* No Claude for a transitioning firm - the server refuses it too. */}
                  {!transitioning && SEAT_REQUESTABLE.includes(u.provision_status) && (
                    <Button variant="link" size="sm" onClick={() => onRequestSeat(u)} title={claudeSignedUp ? 'Request a Claude seat for this member' : "Request a Claude seat (the firm hasn't signed up for Claude yet)"} style={{ color: brand.tealBlue, textDecoration: 'none', fontSize: '0.78rem' }}>
                      <i className="bi bi-plus-circle me-1" />Claude seat
                    </Button>
                  )}
                  {SEAT_PENDING.includes(u.provision_status) && (
                    <Button variant="link" size="sm" onClick={() => onWithdrawSeat(u)} title="Withdraw the seat request (member stays in the hub)" style={{ color: brand.charcoal, textDecoration: 'none', fontSize: '0.78rem' }}>
                      <i className="bi bi-x-circle me-1" />Withdraw
                    </Button>
                  )}
                  {canImpersonate && (
                    <Button variant="link" size="sm" onClick={() => onImpersonate(u)} disabled={impersonatingUuid === u.uuid} title="Log in as this user" style={{ color: brand.tealBlue, textDecoration: 'none' }}>
                      <i className="bi bi-box-arrow-in-right" />
                    </Button>
                  )}
                  <Button variant="link" size="sm" onClick={() => openReset(u)} title="Reset password" style={{ color: brand.deepBlue, textDecoration: 'none' }}>
                    <i className="bi bi-key" />
                  </Button>
                  <Button variant="link" size="sm" onClick={() => onDeactivate(u)} title="Remove user" style={{ color: brand.red, textDecoration: 'none' }}>
                    <i className="bi bi-trash" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Add user modal */}
      <Modal show={showUser} onHide={() => setShowUser(false)} centered>
        <Form onSubmit={submitUser}>
          <Modal.Header closeButton><Modal.Title style={{ fontSize: '1.1rem', color: brand.deepBlue }}>Add member to {firmName}</Modal.Title></Modal.Header>
          <Modal.Body>
            {userError && <Alert variant="danger" className="py-2">{userError}</Alert>}
            {createdInfo ? (
              <div>
                <Alert variant="success" className="py-2">
                  Member added for <strong>{createdInfo.email}</strong>.
                  {createdInfo.claude_requested ? ' A Claude seat was requested.' : ' Hub access only.'}
                  {createdInfo.email_sent
                    ? ' Login instructions were emailed.'
                    : createdInfo.email_error
                      ? ` Email failed: ${createdInfo.email_error}`
                      : ''}
                </Alert>
                <div style={{ fontSize: '0.85rem', color: brand.charcoal }}>Temporary password (copy if not emailed):</div>
                <div style={{ fontFamily: 'monospace', fontSize: '1.05rem', background: brand.softGray, borderRadius: 6, padding: '0.5rem 0.75rem', marginTop: 4 }}>
                  {createdInfo.temp_password}
                </div>
              </div>
            ) : (
              <>
                <Row className="g-2 mb-2">
                  <Col><Form.Group><Form.Label>First name</Form.Label><Form.Control value={userForm.first_name} onChange={e => setUserForm({ ...userForm, first_name: e.target.value })} /></Form.Group></Col>
                  <Col><Form.Group><Form.Label>Last name</Form.Label><Form.Control value={userForm.last_name} onChange={e => setUserForm({ ...userForm, last_name: e.target.value })} /></Form.Group></Col>
                </Row>
                <Form.Group className="mb-2">
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })} placeholder="person@firm.com" />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Role</Form.Label>
                  <Form.Select value={userForm.role} onChange={e => setUserForm({ ...userForm, role: e.target.value })}>
                    <option value="B2B_STANDARD">Standard</option>
                    <option value="B2B_ADMIN">Admin (can manage their firm&apos;s users)</option>
                  </Form.Select>
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  id="firm-user-send-email"
                  label="Email the login invite + temporary password to the user"
                  checked={userForm.send_email}
                  onChange={e => setUserForm({ ...userForm, send_email: e.target.checked })}
                />
                {transitioning ? (
                  <div style={{ fontSize: '0.75rem', color: brand.charcoal, marginTop: 8 }}>
                    This firm is transitioning, so this person will see only the transition dashboard until the firm goes live.
                  </div>
                ) : (
                  <>
                    <Form.Check
                      className="mt-2"
                      type="checkbox"
                      id="firm-user-request-claude"
                      label="Also request a Claude seat for this member"
                      checked={userForm.request_claude}
                      onChange={e => setUserForm({ ...userForm, request_claude: e.target.checked })}
                    />
                    <div style={{ fontSize: '0.75rem', color: brand.charcoal, marginTop: 4, marginLeft: '1.6rem' }}>
                      Leave unticked for hub access only (announcements, resources, training).
                      {!claudeSignedUp && " This firm hasn't signed up for Claude yet."}
                    </div>
                  </>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {createdInfo ? (
              <>
                <Button variant="light" onClick={() => { setCreatedInfo(null); setUserForm(blankUser); }}>Add another</Button>
                <Button style={{ background: brand.deepBlue, border: 'none' }} onClick={() => setShowUser(false)}>Done</Button>
              </>
            ) : (
              <>
                <Button variant="light" onClick={() => setShowUser(false)}>Cancel</Button>
                <Button type="submit" disabled={userSaving} style={{ background: brand.deepBlue, border: 'none' }}>
                  {userSaving ? 'Adding…' : 'Add member'}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Reset password modal */}
      <Modal show={!!resetTarget} onHide={() => setResetTarget(null)} centered>
        <Form onSubmit={submitReset}>
          <Modal.Header closeButton><Modal.Title style={{ fontSize: '1.1rem', color: brand.deepBlue }}>Reset password</Modal.Title></Modal.Header>
          <Modal.Body>
            {resetError && <Alert variant="danger" className="py-2">{resetError}</Alert>}
            {resetResult ? (
              <div>
                <Alert variant="success" className="py-2">
                  Password reset for <strong>{resetTarget?.email}</strong>.
                  {resetResult.email_sent
                    ? ' Login instructions were emailed.'
                    : resetResult.email_error
                      ? ` Email failed: ${resetResult.email_error}`
                      : ''}
                </Alert>
                <div style={{ fontSize: '0.85rem', color: brand.charcoal }}>Temporary password (copy if not emailed):</div>
                <div style={{ fontFamily: 'monospace', fontSize: '1.05rem', background: brand.softGray, borderRadius: 6, padding: '0.5rem 0.75rem', marginTop: 4 }}>
                  {resetResult.temp_password}
                </div>
                <p style={{ fontSize: '0.78rem', color: brand.charcoal, marginTop: '0.75rem', marginBottom: 0 }}>
                  <i className="bi bi-info-circle me-1" />
                  Only shown once — we keep only the hash. The user must set their own password on next sign-in.
                </p>
              </div>
            ) : (
              <>
                <p style={{ fontSize: '0.9rem', color: brand.charcoal }}>
                  Issue a fresh temporary password for{' '}
                  <strong>{[resetTarget?.first_name, resetTarget?.last_name].filter(Boolean).join(' ') || resetTarget?.email}</strong>.
                  Their current password stops working immediately, and they&apos;ll be prompted to set a new one on next sign-in.
                </p>
                <Form.Check
                  type="checkbox"
                  id="firm-reset-send-email"
                  label="Email the temporary password + login instructions to the user"
                  checked={resetSendEmail}
                  onChange={e => setResetSendEmail(e.target.checked)}
                />
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {resetResult ? (
              <Button style={{ background: brand.deepBlue, border: 'none' }} onClick={() => setResetTarget(null)}>Done</Button>
            ) : (
              <>
                <Button variant="light" onClick={() => setResetTarget(null)}>Cancel</Button>
                <Button type="submit" disabled={resetSaving} style={{ background: brand.deepBlue, border: 'none' }}>
                  {resetSaving ? 'Resetting…' : 'Reset password'}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
FirmUsersSection.propTypes = {
  firmId: PropTypes.number.isRequired,
  firmName: PropTypes.string,
  onChange: PropTypes.func,
  canImpersonate: PropTypes.bool,
  claudeSignedUp: PropTypes.bool,
  transitioning: PropTypes.bool,
};
